import React from "react";

/**
 * ToolsMarquee — premium logo strip showing the AI tools used in the course.
 *
 * Pure CSS infinite scroll (no JS rAF loops → zero jitter), GPU-accelerated
 * via translateX. Respects `prefers-reduced-motion` (animation disabled).
 *
 * To add a new tool: drop the optimised SVG/PNG in /public/tools/ and append
 * an entry below. Keep `h-8 md:h-10` heights consistent so the strip stays
 * visually balanced regardless of native logo aspect ratios.
 */
const TOOLS = [
  { name: "ChatGPT",    src: "/tools/chatgpt.svg" },
  { name: "Gemini",     src: "/tools/gemini.svg" },
  { name: "Copilot",    src: "/tools/copilot.png" },
  { name: "NotebookLM", src: "/tools/notebooklm.svg" },
  { name: "Canva",      src: "/tools/canva.png" },
  { name: "Google Meet", src: "/tools/google-meet.svg" },
];

export default function ToolsMarquee() {
  // Duplicate the list so the marquee loop is seamless (translateX -50% lands
  // back on a perfect copy of the first half).
  const loop = [...TOOLS, ...TOOLS];

  return (
    <section
      id="tools"
      aria-labelledby="tools-marquee-title"
      className="scroll-mt-28 pt-2 pb-16 md:pb-20 bg-white"
      data-testid="tools-marquee-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <span
            className="inline-flex items-center rounded-full bg-[#F0E7F4] px-3 py-1 text-xs font-semibold text-[#965EA7] uppercase tracking-widest"
            data-testid="tools-marquee-eyebrow"
          >
            Toolkit-ul cursului
          </span>
          <h3
            id="tools-marquee-title"
            className="mt-4 font-display text-2xl md:text-3xl font-semibold text-[#373A45] tracking-tight"
            data-testid="tools-marquee-title"
          >
            Instrumente utilizate în cadrul cursului
          </h3>
          <p className="mt-3 text-sm md:text-base text-[#6B7280] leading-relaxed">
            Vei lucra practic cu unele dintre cele mai populare platforme AI
            folosite astăzi în educație și în crearea de conținut.
          </p>
        </div>

        {/* Logo strip — full-bleed wrapper for the gradient masks on the edges */}
        <div
          className="relative mt-10 md:mt-12 overflow-hidden"
          style={{
            maskImage:
              "linear-gradient(to right, transparent 0, #000 6%, #000 94%, transparent 100%)",
            WebkitMaskImage:
              "linear-gradient(to right, transparent 0, #000 6%, #000 94%, transparent 100%)",
          }}
          data-testid="tools-marquee-track-wrapper"
        >
          <div
            className="flex w-max items-center gap-12 md:gap-20 animate-marquee-slow hover:[animation-play-state:paused]"
            data-testid="tools-marquee-track"
          >
            {loop.map((tool, i) => (
              <div
                key={`${tool.name}-${i}`}
                className="shrink-0 flex items-center justify-center"
                data-testid={i < TOOLS.length ? `tools-marquee-item-${i}` : undefined}
                aria-hidden={i >= TOOLS.length ? "true" : undefined}
              >
                <img
                  src={tool.src}
                  alt={i < TOOLS.length ? tool.name : ""}
                  loading="lazy"
                  decoding="async"
                  draggable={false}
                  className="h-7 md:h-9 w-auto select-none opacity-70 grayscale transition-all duration-500 hover:opacity-100 hover:grayscale-0"
                  style={{ maxWidth: "180px" }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
